import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { eventsService, Event } from '../services/firebase/events.service';
import { userService } from '../services/firebase/user.service';
import { useSubscription } from '../components/subscription/SubscriptionComponents';
import { detectDuplicateEvents } from '../utils/event-validation';
import { useAuth } from './AuthContext';

// Interface pour le contexte
interface EventContextType {
  currentEvent: Event | null;
  events: Event[];
  loading: boolean;
  error: string | null;
  switchEvent: (eventId: string) => Promise<boolean>;
  createEvent: (eventData: { name: string; date?: string | null; type: string; location?: string }) => Promise<Event | null>;
  updateEvent: (eventId: string, updates: Partial<Event>) => Promise<boolean>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  refreshEvents: () => Promise<void>;
}

// Création du contexte
const EventContext = createContext<EventContextType | undefined>(undefined);

// Props pour le provider
interface EventProviderProps {
  children: ReactNode;
}

// Provider component
export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { loading: subscriptionLoading } = useSubscription();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Charger les événements quand l'authentification est prête
  useEffect(() => {
    if (!authLoading && !subscriptionLoading) {
      if (currentUser) {
        loadEvents();
      } else {
        setEvents([]);
        setCurrentEvent(null);
        setLoading(false);
      }
    }
  }, [authLoading, currentUser?.uid, subscriptionLoading]);

  // Fonction pour charger les événements de l'utilisateur
  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        setEvents([]);
        setCurrentEvent(null);
        setLoading(false);
        return;
      }
      
      try {
        const userEvents = await eventsService.getUserEvents(currentUser.uid);
        
        if (userEvents.length === 0) {
          // ⚠️ DÉSACTIVÉ : Ne plus créer d'événement classique automatiquement
          // Les nouveaux utilisateurs sont maintenant redirigés vers la création d'événements génériques
          // const defaultEvent = await eventsService.createDefaultEvent(user.uid);
          // 
          // if (defaultEvent) {
          //   setEvents([defaultEvent]);
          //   setCurrentEvent(defaultEvent);
          // }
          
          // Laisser la liste vide pour les nouveaux utilisateurs
          setEvents([]);
          setCurrentEvent(null);
        } else {
          setEvents(userEvents);
          
          const defaultEvent = userEvents.find(event => event.isDefault) || userEvents[0];
          
          if (defaultEvent) {
            setCurrentEvent(defaultEvent);
            
            if (!defaultEvent.isDefault) {
              await eventsService.setDefaultEvent(currentUser.uid, defaultEvent.id);
            }
          }
        }
      } catch (eventsError) {
        setError('Erreur lors du chargement des événements');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur s\'est produite lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir la liste des événements
  const refreshEvents = async () => {
    await loadEvents();
  };

  // Fonction pour basculer entre les événements
  const switchEvent = async (eventId: string): Promise<boolean> => {
    try {
      if (currentEvent?.id === eventId) {
        return true;
      }
      
      const eventToSwitch = events.find(e => e.id === eventId);
      
      if (!eventToSwitch) {
        throw new Error(`L'événement avec l'ID ${eventId} n'existe pas`);
      }
      
      if (!currentUser) {
        throw new Error('Utilisateur non connecté');
      }
      
      await eventsService.setDefaultEvent(currentUser.uid, eventId);
      
      setCurrentEvent(eventToSwitch);
      
      const updatedEvents = events.map(e => ({
        ...e,
        isDefault: e.id === eventId
      }));
      
      setEvents(updatedEvents);
      
      alert(t('events.switchedTo', { name: eventToSwitch.name }));
      
      navigate('/');
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur s\'est produite lors du changement d\'événement');
      return false;
    }
  };

  // Fonction pour créer un nouvel événement
  const createEvent = async (eventData: { name: string; date?: string | null; type: string; location?: string }): Promise<Event | null> => {
    if (!currentUser) {
      throw new Error('Utilisateur non connecté');
    }

    setLoading(true);
    setError(null);

    try {
      const duplicateCheck = await detectDuplicateEvents({ 
        name: eventData.name, 
        type: eventData.type, 
        date: eventData.date || null
      });
      if (duplicateCheck.hasDuplicates && duplicateCheck.duplicates.length > 0) {
        throw new Error(`Un événement similaire existe déjà: "${duplicateCheck.duplicates[0]?.name}"`);
      }

      // Pour l'instant, créer l'événement avec l'ancien système (events collection)
      // Les nouveaux événements créés via le modal utilisent directement le service générique
      const newEvent = await eventsService.createEvent(currentUser.uid, eventData);
      
      if (newEvent) {
        setEvents(prevEvents => [...prevEvents, newEvent]);
        
        if (events.length === 0) {
          setCurrentEvent(newEvent);
        }
      }
      
      return newEvent;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur s\'est produite lors de la création de l\'événement');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour un événement
  const updateEvent = async (eventId: string, updates: Partial<Event>): Promise<boolean> => {
    try {
      const success = await eventsService.updateEvent(eventId, updates);
      
      if (success) {
        const updatedEvents = events.map(event => 
          event.id === eventId ? { ...event, ...updates, updatedAt: new Date() } : event
        );
        
        setEvents(updatedEvents);
        
        if (currentEvent?.id === eventId) {
          setCurrentEvent({ ...currentEvent, ...updates, updatedAt: new Date() });
        }
      }
      
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur s\'est produite lors de la mise à jour de l\'événement');
      return false;
    }
  };

  // Fonction pour supprimer un événement
  const deleteEvent = async (eventId: string): Promise<boolean> => {
    try {
      if (currentEvent?.id === eventId) {
        throw new Error('Vous ne pouvez pas supprimer l\'événement par défaut. Veuillez d\'abord sélectionner un autre événement comme événement par défaut.');
      }
      
      const success = await eventsService.deleteEvent(eventId);
      
      if (success) {
        setEvents(events.filter(event => event.id !== eventId));
      }
      
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur s\'est produite lors de la suppression de l\'événement');
      return false;
    }
  };

  return (
    <EventContext.Provider
      value={{
        currentEvent,
        events,
        loading,
        error,
        switchEvent,
        createEvent,
        updateEvent,
        deleteEvent,
        refreshEvents
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useEvent = (): EventContextType => {
  const context = useContext(EventContext);
  
  if (context === undefined) {
    throw new Error('useEvent doit être utilisé à l\'intérieur d\'un EventProvider');
  }
  
  return context;
};

export default EventContext; 